/**
 * Workflow 설정 전용 레이아웃
 * @description services/서비스-프론트엔드-개발가이드.md 준수 — UnifiedLayout + 왼쪽 사이드바 탭
 */

import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { UnifiedLayout } from '@shared/ui/layout';
import type { SidebarTab } from '@shared/ui/layout';
import { makeMenuIcon, MenuPngIcon } from '@/lib/menuIcons';
import { WorkflowSettingsPage } from '@/features/admin/pages/WorkflowSettingsPage';
import { useAuthStore } from '@/stores/authStore';
import { useTenantPortalLogo } from '@/hooks/useTenantPortalLogo';

const WORKFLOW_CATEGORY_ORDER = ['유형 정의', '대상·결재']

const WORKFLOW_SIDEBAR_TABS: SidebarTab[] = [
  { id: 'service-types', label: 'Workflow 서비스 유형', icon: makeMenuIcon('wf-types'), href: '/workflow-settings', labelType: ['유형 정의'] },
  { id: 'request-types', label: 'Workflow 서비스', icon: makeMenuIcon('wf-services'), href: '/workflow-settings', labelType: ['유형 정의'] },
  { id: 'domain-visibility', label: '요청 유형별 사용 대상', icon: makeMenuIcon('wf-audience'), href: '/workflow-settings', labelType: ['대상·결재'] },
  { id: 'approval-flows', label: '결재 플로우', icon: makeMenuIcon('wf-approval'), href: '/workflow-settings', labelType: ['대상·결재'] },
];

export function WorkflowSettingsLayout() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, refreshToken } = useAuthStore();
  const [activeSection, setActiveSection] = useState<string>(WORKFLOW_SIDEBAR_TABS[0].id);
  const tenantLogo = useTenantPortalLogo();

  const handleLogoClick = () => {
    navigate({ to: '/entry' });
  };

  const handleTabClick = (tab: SidebarTab) => {
    setActiveSection(tab.id);
  };

  return (
    <UnifiedLayout
      entryUrl="/entry"
      logoText={tenantLogo || 'IAMPAM PORTAL'}
      onLogoClick={handleLogoClick}
      user={user}
      isAuthenticated={isAuthenticated}
      onLogout={logout}
      onExtendToken={async () => {
        const ok = await refreshToken();
        if (!ok) {
          alert('세션 연장에 실패했습니다. 서버가 실행 중인지 확인하세요. (localhost:3100)');
        }
      }}
      serviceName="Workflow 설정"
      serviceHref="/workflow-settings"
      sidebar={{
        categoryOrder: WORKFLOW_CATEGORY_ORDER,
        tabs: WORKFLOW_SIDEBAR_TABS,
        activeTab: activeSection,
        collapsible: true,
        onTabClick: handleTabClick,
        subtitle: 'Workflow 설정',
        expandedWidthRem: 18,
        headerIcon: <MenuPngIcon slug="wf-hero" className="h-7 w-7 shrink-0 object-contain" />,
      }}
      breadcrumbItems={[
        { label: 'Workflow 설정', href: '/workflow-settings' },
        { label: WORKFLOW_SIDEBAR_TABS.find((t) => t.id === activeSection)?.label ?? activeSection },
      ]}
    >
      <div className="p-6">
        <WorkflowSettingsPage activeSection={activeSection} onSectionChange={setActiveSection} />
      </div>
    </UnifiedLayout>
  );
}
